/* eslint-disable complexity */
import { get } from '../../../common/utils/objectUtils';
import { formatPrice } from '../../../common/formatting/formattingService';
import { ProductImageTag } from '../productdetail/types';
import { ProductInterface, ProductResponse, PriceFormatConfigOptions, ApiResponseAppliedFilters } from './types';
import { AppliedSorting } from 'Client/components/Generic/FilterAndSortBox/types';

interface Simple {
    /* eslint-disable @typescript-eslint/camelcase, camelcase */
    bestSellerSKU?: string;
    sku: string;
    price: number;
    original_price: number;
    image_url?: string;
    alt_tag: string;
    available: boolean;
    image?: {
        path: string;
        seo_url: string;
        tag: ProductImageTag;
    };
    /* eslint-enable @typescript-eslint/camelcase, camelcase */
}

interface SimpleOptions {
    defaultSimple: Simple;
    pdpSimple?: Simple;
}

const PRODUCT_SCORE_SORT_TYPE = 'score_product_boost';
const PRICE_SORT_TYPE = 'facet_price';
const DESC_SORT_VALUE = 'desc';
const ASC_SORT_VALUE = 'asc';

export const formatProduct = (
    position: number,
    product: ProductResponse,
    defaultSimple: Simple,
    pdpSimple?: Simple,
    formatOptions?: PriceFormatConfigOptions
    // eslint-disable-next-line max-params
): ProductInterface => {
    let price = String(defaultSimple.price);
    let originalPrice = String(defaultSimple.original_price);
    const link = pdpSimple ? `/${product.meta.pdp_url}?simple=${pdpSimple.sku}` : `/${product.meta.pdp_url}`;

    if (formatOptions) {
        const { countryCode, currency } = formatOptions;
        price = String(formatPrice(countryCode, currency, defaultSimple.price));
        originalPrice = formatPrice(countryCode, currency, defaultSimple.original_price);
    }

    let bestSellerImage;

    if (formatOptions?.showStrongest && defaultSimple.bestSellerSKU) {
        const bestSellerSimple = { ...product.simples[defaultSimple.bestSellerSKU].meta };
        bestSellerImage = {
            path: get(bestSellerSimple, 'image.path', ''),
            seoUrl: get(bestSellerSimple, 'image.seo_url', ''),
            tag: get(bestSellerSimple, 'image.tag', ''),
        };
    }

    return {
        sku: product.meta.sku,
        simpleSku: defaultSimple.sku,
        baseImage: defaultSimple.image_url || product.meta.image_url || '',
        name: product.meta.name,
        altTag: defaultSimple.alt_tag || product.meta.alt_tag,
        brand: product.meta.brand,
        blackFriday: product.meta.black_friday_top_deal || false,
        cyberMonday: product.meta.cyber_monday_top_deal || false,
        isSustainable: product.meta.is_sustainable,
        link,
        simplesCount: Object.keys(product.simples).length,
        quantityBadge: get(product, 'meta.quantity_badge_number', 0),
        position,
        price,
        originalPrice,
        soldOut: product.meta.soldout || false,
        productImage: {
            path: get(defaultSimple, 'image.path', ''),
            seoUrl: get(defaultSimple, 'image.seo_url', ''),
            tag: get(defaultSimple, 'image.tag', ''),
        },
        bestSellerImage,
        showBestsellerImage: product.meta.show_bestseller_image || false,
    };
};

const applySorting = (sorting: AppliedSorting, simples: Array<Simple>): SimpleOptions => {
    const defaultSimple = simples[0];
    const mostExpensiveSimple = simples[simples.length - 1];
    const { name, order } = sorting;
    const sortedByPriceAscending = name === PRICE_SORT_TYPE && order === ASC_SORT_VALUE;
    const sortedByPriceDescending = name === PRICE_SORT_TYPE && order === DESC_SORT_VALUE;
    const sortedByProductScore = name === PRODUCT_SCORE_SORT_TYPE;
    let simpleOptions: SimpleOptions = { defaultSimple, pdpSimple: defaultSimple };

    if (sortedByProductScore || sortedByPriceAscending) {
        const availableSimples = simples.filter(simple => simple.available);
        const cheapestAvailableSimple = availableSimples.length ? availableSimples[0] : defaultSimple;
        simpleOptions = { defaultSimple, pdpSimple: cheapestAvailableSimple };
    }

    if (sortedByPriceDescending) {
        simpleOptions = { defaultSimple: mostExpensiveSimple, pdpSimple: mostExpensiveSimple };
    }

    return simpleOptions;
};

const mapResponseToProduct = (
    position: number,
    product: ProductResponse,
    sortingParams?: AppliedSorting,
    filters?: ApiResponseAppliedFilters,
    formatOptions?: PriceFormatConfigOptions
    // eslint-disable-next-line max-params
): ProductInterface => {
    const simplesArray: Array<Simple> = [];

    Object.keys(product.simples).forEach(sku => {
        simplesArray.push({ ...product.simples[sku].meta, sku });
    });

    // Sort SimplesArray by price in ascending order
    const sortedSimples = simplesArray.sort((a: Simple, b: Simple) => (a.price! >= b.price! ? 1 : -1));
    // Cannot depend on API cheapest simple SKU as bestseller API does not have it.
    const bestSellerSKU = product.meta.best_selling_simple_sku;

    let simpleOptions: SimpleOptions = {
        defaultSimple: { ...sortedSimples[0], bestSellerSKU },
    };

    if (filters && Object.keys(filters).length) {
        simpleOptions.pdpSimple = simpleOptions.defaultSimple;
    }

    if (sortingParams && Object.keys(sortingParams).length) {
        simpleOptions = applySorting(sortingParams, sortedSimples);
    }

    return formatProduct(position, product, simpleOptions.defaultSimple, simpleOptions.pdpSimple, formatOptions);
};

export default mapResponseToProduct;
